import React from 'react';
import PropTypes from 'prop-types';
import store from '@Store';
import InnerHTML from '@Components/InnerHTML';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ToggleButton from '@Components/Results/reactions/reaction-details/ToggleButton';
import ReactionCIStageDetailsComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-ci-stage-details.component';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import ReactionStageDetailComponent from '@Components/Results/reactions/reaction-details/reaction-stage-detail.component';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ConditionsReferenceExperimentComponent from '@Components/Results/reactions/reaction-details/ConditionsReferenceExperiment';
import ReactionAdditionalInfoComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-additional-info.component';
import { openMultiStepReactionDialog } from '@Pages/RetroSynthesis/MultiStepReactionDialog/actions';
import { Checkbox } from '@els/els-react--checkbox';
import { CITATION_LINKS_TRACKING_INDEX_MARKER } from '@Components/Results/analytics/citationLinksTracking';

import './ReactionDetailRow.scss';

export default class ReactionDetailRowComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      experimentalProcedureExpanded: false,
      referencesForcedShow: false,
    };

    const { databaseId, detail } = this.props;

    this.isCIDatabase = ReactReactionAdditionalInfoHelper.isCIDatabase(databaseId);
    this.additionalInfoVisible =
      props.allowAdditionalInfoShow && this.isCIDatabase && ReactReactionAdditionalInfoHelper.isAdditionalInfoVisible(detail);

    this.goToMultipleStepScheme = this.goToMultipleStepScheme.bind(this);
    this.toggleExperimentalProcedureExpanded = this.toggleExperimentalProcedureExpanded.bind(this);
    this.toggleReferencesForcedShow = this.toggleReferencesForcedShow.bind(this);
  }

  componentDidMount() {
    this.recalculateRenderedRowsNumber();
  }

  componentDidUpdate(prevProps, prevState) {
    const { experimentalProcedureExpanded, referencesForcedShow } = this.state;
    if (
      prevState.experimentalProcedureExpanded !== experimentalProcedureExpanded ||
      prevState.referencesForcedShow !== referencesForcedShow
    ) {
      this.recalculateRenderedRowsNumber();
    }
  }

  getYields() {
    const yields = get(this, 'props.detail.yields', null);
    return yields
      ? yields.map((yiel, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="yield-row" key={index}>
            {yields.length > 1 && <strong>{ReactReactionDetailsHelper.getLetterTitle(index)}&nbsp;</strong>}
            <InnerHTML tag="span" html={(yiel && yiel.yieldValue) || 'n/a'} />
          </div>
        ))
      : null;
  }

  getMultiStepDetails() {
    const multiStepDetails = get(this, 'props.detail.multiStepDetails', null);
    return multiStepDetails
      ? multiStepDetails.map((step, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <InnerHTML tag="div" key={index} className="steps-row" html={step} mode="escape" />
        ))
      : null;
  }

  getExampleLabelTitle() {
    const { detail } = this.props;
    const lbl = detail.exampleLabel;
    const ttl = detail.exampleTitle;
    return `${lbl || ''} ${ttl || ''}`.trim() || 'Experimental Procedure';
  }

  getTrackingProps() {
    const trackingProps = {};
    const { metadata, detail } = this.props;
    if (!metadata.oldTrackingDisabled) {
      trackingProps.prepareTrackingData = !isEmpty(detail.citations) ? detail.citations?.[0] : null;
      trackingProps.dataTrackingAction = 'experimental procedure';
    }
    return trackingProps;
  }

  // Calculates number of rendered <tr>s
  recalculateRenderedRowsNumber() {
    const { experimentalProcedureExpanded, referencesForcedShow } = this.state;
    const { onRenderedRowsNumberChange } = this.props;
    const renderedRows =
      1 + (experimentalProcedureExpanded ? 1 : 0) + (referencesForcedShow ? 2 : 0) + (this.additionalInfoVisible ? 1 : 0);
    onRenderedRowsNumberChange(renderedRows);
  }

  toggleExperimentalProcedureExpanded() {
    this.setState(
      (prevState) => ({
        experimentalProcedureExpanded: !prevState.experimentalProcedureExpanded,
      }),
      () => {
        const { experimentalProcedureExpanded } = this.state;
        const { detail, onExperimentalProcedureOpen } = this.props;
        if (experimentalProcedureExpanded && !isEmpty(detail.citations)) {
          onExperimentalProcedureOpen(detail.citations?.[0]);
        }
      }
    );
  }

  toggleReferencesForcedShow() {
    this.setState((prevState) => ({
      referencesForcedShow: !prevState.referencesForcedShow,
    }));
  }

  goToMultipleStepScheme() {
    const { detail, metadata, rtProjectId, rtRouteId, rtParentId, rtStepId } = this.props;

    if (!this.schemeLoading) {
      this.schemeLoading = true;
      store.dispatch(
        openMultiStepReactionDialog({
          databaseId: metadata && metadata.databaseId,
          scheme: detail && detail.scheme,
          onClose: () => {
            this.schemeLoading = false;
          },
          rtProjectId,
          rtRouteId,
          rtParentId,
          rtStepId,
        })
      );
    }
  }

  render() {
    const {
      isHit,
      metadata,
      detail,
      index,
      largestProduct,
      onFullTextLinkClick,
      onIssueCitedCounterClick,
      onAbstractOpen,
      onAuthorClick,
      databaseId,
      reactionId,
      renderCheckboxesColumn,
      isChecked,
      isVisible,
      setChecked,
      rtStepId,
    } = this.props;
    const { experimentalProcedureExpanded, referencesForcedShow } = this.state;
    const mainRowClasses = classnames([
      'c-els-table__row',
      isHit ? 'rx-table__group--highlight' : null,
      experimentalProcedureExpanded ? 'no-border-bottom rx-reactions-table__row-expanded' : null,
      isVisible ? null : 'rx-reactions-table__row-not-visible',
    ]);

    return (
      <>
        <tr className={mainRowClasses} {...(!referencesForcedShow && CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem)}>
          {/* Conditions section */}
          {renderCheckboxesColumn && (
            <td className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__checkbox">
              <Checkbox
                id={`checkbox-${reactionId}_${index}`}
                label={`Select ${index}`}
                shouldHideLabel
                checked={isChecked}
                onChange={() => setChecked(detail.id, !isChecked)}
              />
            </td>
          )}
          <td className="c-els-table__cell c-els-table__basic-cell">
            <div className="rx-reactions-table__conditions__steps">
              {detail.multiStepDetails ? (
                <>
                  <div className="steps-row">
                    <InnerHTML tag="b" html={detail.classification?.[0]} extendedTags />
                    <b> with </b>
                    <InnerHTML tag="b" html={detail.stepsCount} extendedTags />
                    <b> steps </b>
                  </div>
                  {this.getMultiStepDetails()}
                  {detail.scheme && (
                    <button
                      type="button"
                      className="els-btn els-btn-xs els-btn-link blue e2e-view-scheme"
                      onClick={this.goToMultipleStepScheme}>
                      <span>{rtStepId ? 'View/Insert Scheme' : 'View Scheme'}</span>
                      <svg className="els-btn-icon els-btn-icon-tiny">
                        <use xlinkHref="#icon-navigate-right" />
                      </svg>
                    </button>
                  )}
                </>
              ) : (
                <>
                  {detail.stages &&
                    detail.stages?.map((stage, stageIndex, stages) => (
                      /* eslint-disable react/no-array-index-key */
                      <ReactionStageDetailComponent
                        metadata={metadata}
                        key={stageIndex}
                        index={index}
                        stage={stage}
                        stageIndex={stageIndex}
                        isCIDatabase={this.isCIDatabase}
                        hasMultipleStages={stages.length > 1}
                      />
                      /* eslint-enable react/no-array-index-key */
                    ))}

                  {this.isCIDatabase && <ReactionCIStageDetailsComponent largestProduct={largestProduct} detail={detail} />}
                </>
              )}
            </div>

            {/* Toggle buttons (Experimental Procedure and Reference) */}
            <div className="els-btn-group-left">
              {detail.experimentalProcedure && (
                <ToggleButton
                  className="rx-reactions-table__content-toggle els-btn-xs e2e-experimental-procedure"
                  ariaControls={`reaction-procedure-${index}`}
                  expanded={experimentalProcedureExpanded}
                  toggleExpand={this.toggleExperimentalProcedureExpanded}
                  expandedLabel="Experimental Procedure"
                  collapsedLabel="Experimental Procedure"
                  isViewPreview={metadata.isViewPreview}
                  {...this.getTrackingProps()}
                />
              )}
              {(!isEmpty(detail.citations) || detail.elnCitation || detail.experiment) && (
                <ToggleButton
                  className="rx-reactions-table__content-toggle els-btn-xs u-els-display-none u-els-display-table@tablet e2e-show-hide-reference"
                  ariaControls={`reaction-references-${index}`}
                  expanded={referencesForcedShow}
                  toggleExpand={this.toggleReferencesForcedShow}
                  expandedLabel={this.isCIDatabase ? 'Hide Reference/Experiment' : 'Hide Reference'}
                  collapsedLabel={this.isCIDatabase ? 'Show Reference/Experiment' : 'Show Reference'}
                  isViewPreview={metadata.isViewPreview}
                />
              )}
            </div>
          </td>

          {/* Yields section */}
          <td className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__yield">{this.getYields()}</td>

          {/* Reference/Experiment section - wide screen */}
          <td className="c-els-table__cell c-els-table__basic-cell u-els-display-none@tablet" id={`reaction-references-${index}`}>
            <ConditionsReferenceExperimentComponent
              metadata={metadata}
              index={index}
              detail={detail}
              onFullTextLinkClick={onFullTextLinkClick}
              onIssueCitedCounterClick={onIssueCitedCounterClick}
              onAbstractOpen={onAbstractOpen}
              onAuthorClick={onAuthorClick}
            />
          </td>
        </tr>

        {/* Experimental procedure section */}
        {experimentalProcedureExpanded && (
          <tr className={` c-els-table__row display-table-row ${isVisible ? '' : 'rx-reactions-table__row-not-visible'}`}>
            {renderCheckboxesColumn && <th className="rx-reactions-table__extra" />}
            <td
              className="table__cell c-els-table__cell c-els-table__basic-cell"
              colSpan={renderCheckboxesColumn ? 3 : 4}
              id={`reaction-procedure-${index}`}>
              <div className="rx-reactions-table__header">{this.getExampleLabelTitle()}</div>
              <InnerHTML tag="div" className="rx-reactions-table__content" html={detail.experimentalProcedure} extendedTags />
            </td>
          </tr>
        )}

        {/* Reference/Experiment section - toggled on small screen */}
        {referencesForcedShow && (
          <>
            <tr className=" c-els-table__row rx-reactions-table__header display-table-row display-table-row-at-tablet">
              <td
                colSpan="4"
                className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__references u-els-display-none@tablet">
                {this.isCIDatabase ? 'Reference/Experiment' : 'Reference'}
              </td>
            </tr>

            <tr
              className={`c-els-table__row rx-reactions-table__item display-table-row-at-tablet${isHit ? ' rx-table__group--highlight' : ''}`}
              {...CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem}>
              <td className="c-els-table__cell c-els-table__basic-cell" colSpan="4" id={`reaction-references-${index}`}>
                <ConditionsReferenceExperimentComponent
                  metadata={metadata}
                  index={index}
                  detail={detail}
                  onFullTextLinkClick={onFullTextLinkClick}
                  onIssueCitedCounterClick={onIssueCitedCounterClick}
                  onAbstractOpen={onAbstractOpen}
                  onAuthorClick={onAuthorClick}
                />
              </td>
            </tr>
          </>
        )}

        {/* Additional CI info section */}
        {this.additionalInfoVisible && (
          <tr className={` c-els-table__row rx-reactions-table__item display-table-row${isHit ? ' rx-table__group--highlight' : ''}`}>
            <td className=" c-els-table__cell c-els-table__basic-cell rx-reactions-table__additional-info" colSpan="4">
              <ReactionAdditionalInfoComponent detail={detail} databaseId={databaseId} index={index} reactionId={reactionId} />
            </td>
          </tr>
        )}
      </>
    );
  }
}

ReactionDetailRowComponent.defaultProps = {
  onExperimentalProcedureOpen: () => null,
  onFullTextLinkClick: () => null,
  onIssueCitedCounterClick: () => null,
  onAbstractOpen: () => null,
  onAuthorClick: () => null,
  allowAdditionalInfoShow: true,
  renderCheckboxesColumn: false,
  isVisible: true,
  onRenderedRowsNumberChange: () => null,
};

ReactionDetailRowComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  detail: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
  reactionId: PropTypes.string.isRequired,
  databaseId: PropTypes.string.isRequired,
  isHit: PropTypes.bool,
  spinnerId: PropTypes.string,
  largestProduct: PropTypes.string,
  onExperimentalProcedureOpen: PropTypes.func,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
  allowAdditionalInfoShow: PropTypes.bool,
  renderCheckboxesColumn: PropTypes.bool,
  isChecked: PropTypes.bool,
  isVisible: PropTypes.bool,
  setChecked: PropTypes.func,
  onRenderedRowsNumberChange: PropTypes.func,
};
